// Lib
import gql from 'graphql-tag';

// Types
import {
  AUTH,
  AUTH_NOTE,
  CAMPAIGN,
  CAMPAIGN_OFFLINE_REVENUE,
  CAMPAIGN_PAYMENT,
} from '../types';

export const campaignCreate = gql`
  mutation CampaignCreate($input: CampaignBuildInput) {
    campaignCreate(input: $input) {
      ${CAMPAIGN}
    }
  }
`;

export const campaignEdit = gql`
  mutation CampaignEdit($input: CampaignUpdateInput) {
    campaignEdit(input: $input) {
      ${CAMPAIGN}
    }
  }
`;

// #713 / sweep (#706): fire the on-demand campaign hashtag/mention sweep. Runs
// regardless of the campaign's `sweepEnabled` (that flag only gates the daily
// scheduled worker). Returns the job status; the FE then polls
// `campaignSweepStatus` while `state == RUNNING`.
export const runCampaignSweep = gql`
  mutation RunCampaignSweep($input: RunCampaignSweepInput!) {
    runCampaignSweep(input: $input) {
      campaignId
      state
      startedAt
      finishedAt
      message
      cooldownSecondsRemaining
      stats {
        scanned
        discoveredNew
        postsAttributed
        membersHit
        refreshed
        pagesFetched
        capped
      }
    }
  }
`;

export const campaignFinish = gql`
  mutation CampaignFinish($input: CampaignFinishInput) {
    campaignFinish(input: $input) {
      ${CAMPAIGN}
    }
  }
`;

// Soft-deletes (archives) a campaign: sets `isDeleted` so it's hidden from the
// board / lists. Reversible — distinct from `campaignFinish` (which transitions
// status). Returns Boolean!. Types are generated into src/types/graphql.ts
// (GqlCampaignDeleteInput / GqlMutationRootCampaignDeleteArgs).
export const campaignDelete = gql`
  mutation CampaignDelete($input: CampaignDeleteInput!) {
    campaignDelete(input: $input)
  }
`;

// Transitions campaign status `Setup → Draft`. Idempotent on already-Draft
// rows. Called from DraftSetupView after the user clicks "Confirm draft".
export const markCampaignSetupReviewed = gql`
  mutation MarkCampaignSetupReviewed($campaignId: String!) {
    markCampaignSetupReviewed(campaignId: $campaignId) {
      ${CAMPAIGN}
    }
  }
`;

// AI-assisted campaign creation. Creates a placeholder campaign
// (`name="Untitled campaign", status=setup`) and starts an Automation
// AIChat session. Frontend navigates the user to `/ai-chat/${chatSessionId}`
// — the agent fills the rest via campaign_update tool calls.
export const campaignCreateWithChat = gql`
  mutation CampaignCreateWithChat($input: CampaignCreateWithChatInput!) {
    campaignCreateWithChat(input: $input) {
      campaign {
        _id
        name
        status
      }
      chatSessionId
    }
  }
`;

export const campaignAuthNoteCreate = gql`
  mutation CampaignAuthNoteCreate($input: AuthNoteInput) {
    authNoteCreate(input: $input) {
      ${AUTH_NOTE}
    }
  }
`;

export const campaignAuthNoteEdit = gql`
  mutation CampaignAuthNoteEdit($input: AuthNoteInput) {
    authNoteEdit(input: $input) {
      ${AUTH_NOTE}
    }
  }
`;

export const authIndex = gql`
  mutation AuthIndex($input: AuthPrefetchInput) {
    authIndex(input: $input) {
      ${AUTH}
    }
  }
`;

export const campaignPaymentCreate = gql`
  mutation CampaignPaymentCreate($input: CampaignPaymentCreateInput!) {
    campaignPaymentCreate(input: $input) {
      ${CAMPAIGN_PAYMENT}
    }
  }
`;

export const campaignPaymentDelete = gql`
  mutation CampaignPaymentDelete($input: CampaignPaymentDeleteInput) {
    campaignPaymentDelete(input: $input)
  }
`;

export const campaignOfflineRevenueCreate = gql`
  mutation CampaignOfflineRevenueCreate($input: CampaignOfflineRevenueCreateInput) {
    campaignOfflineRevenueCreate(input: $input) {
      ${CAMPAIGN_OFFLINE_REVENUE}
    }
  }
`;

export const campaignOfflineRevenueDelete = gql`
  mutation CampaignOfflineRevenueDelete(
    $input: CampaignOfflineRevenueDeleteInput
  ) {
    campaignOfflineRevenueDelete(input: $input)
  }
`;

export const authCreatorStatusCreate = gql`
  mutation AuthCreatorStatusCreate($input: AuthCreatorStatusInput) {
    authCreatorStatusCreate(input: $input) {
      _id
      createdAt
      updatedAt
      authId
      status
    }
  }
`;

export const authCreatorStatusEdit = gql`
  mutation AuthCreatorStatusEdit($input: AuthCreatorStatusInput) {
    authCreatorStatusEdit(input: $input) {
      _id
      createdAt
      updatedAt
      authId
      status
    }
  }
`;

export const authRefetch = gql`
  mutation AuthRefetch($input: AuthRefetchInput) {
    authRefetch(input: $input)
  }
`;

export const campaignAuthsUpdateBulk = gql`
  mutation CampaignAuthsUpdateBulk($input: CampaignAuthsUpdateBulkInput!) {
    campaignAuthsUpdateBulk(input: $input)
  }
`;

export const campaignStart = gql`
  mutation CampaignStart($input: CampaignStartInput!) {
    campaignStart(input: $input) {
      ${CAMPAIGN}
    }
  }
`;

export const campaignExportCreate = gql`
  mutation CampaignExportCreate(
    $campaignId: ID!
    $options: CampaignExportOptionsInput
  ) {
    campaignExportCreate(campaignId: $campaignId, options: $options) {
      id
      downloadUrl
      partial
      sizeBytes
      sliceWarnings {
        slice
        reason
      }
    }
  }
`;

export const updateCampaignAuthOutreachStatus = gql`
  mutation UpdateCampaignAuthOutreachStatus(
    $input: UpdateCampaignAuthOutreachStatusInput!
  ) {
    updateCampaignAuthOutreachStatus(input: $input) {
      status
    }
  }
`;
